import {
  useGetTeamsQuery,
  useUpdateTeamMutation,
} from 'store/slices/apiV1/properties';
import {
  BooleanIconRenderCell,
  RelatedRecordRenderCell,
} from 'ui-component/DataGrid/Render';
import {
  getApplyFilterFnRelatedRecords,
  relatedRecordsFilterOperators,
} from 'ui-component/DataGrid/FilterOperators';
import { relatedRecordsComparator } from 'ui-component/DataGrid/SortComparators';
import { ALL_APP_IDS, ALL_APPS } from 'constants/appConstants';
import {
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { orgFromUserOrgId } from 'utils/userPermissions';
import { useMemo, useState } from 'react';
import _ from 'lodash';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { useSelector } from 'store';
import { ExtendedGridColDef, DatagridNames, RowAction } from 'types/datagrid';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { appPermissionAccessLevels } from 'types/apps';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import CreateTeamDialog from 'views/properties/Teams/components/CreateTeamDialog';
import useGetCustomFieldColumns from 'hooks/useGetCustomFieldColumns';
import { RecordTypes } from 'ui-component/DetailCustomFields';
import { IconArchive, IconArchiveOff, IconPencil } from '@tabler/icons-react';
import { TeamLite } from 'types/properties';
import UpdateTeamDialog from 'views/properties/Teams/components/UpdateTeamDialog';
import { EditInlineTeamTypeAutocomplete } from 'ui-component/HookFormComponents/InlineEditInputComponents';
import { GridRenderEditCellParams } from '@mui/x-data-grid';
import { FieldValues } from 'react-hook-form';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import ArchiveDialog from 'ui-component/ArchiveDialog';
import { ManagementActionTypes } from 'types';
import { useDialogManager } from 'hooks/useDialogManager';

enum TEAMS_DIALOGS {
  CREATE = 'create',
  UPDATE = 'update',
  ARCHIVE = 'archive',
}

const TeamsTable = () => {
  const { data: user } = useGetUserQuery(undefined);
  const { activeOrgId } = useSelector((state) => state.org);

  const [teamToUpdate, setTeamToUpdate] = useState<TeamLite | null>(null);
  const { openDialog, closeDialog, currentDialog } = useDialogManager(
    Object.values(TEAMS_DIALOGS)
  );

  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();
  const { data: teams, isLoading: isLoadingTeams } = useGetTeamsQuery();

  const otherOrgs = useMemo(
    () => _.filter(teams, (team) => team.org !== activeOrgId).length,
    [activeOrgId, teams]
  );

  const { isPermissionDisabled } = useAppAccessContext();
  const editDisabled = isPermissionDisabled({
    app: ALL_APP_IDS.PROPERTIES,
    accessLevel: appPermissionAccessLevels.edit,
  });

  const handleOpenUpdateTeamDialog = (rowTeam: TeamLite) => {
    openDialog(TEAMS_DIALOGS.UPDATE);
    setTeamToUpdate(rowTeam);
  };

  const handleOpenArchiveDialog = (rowTeam: TeamLite) => {
    openDialog(TEAMS_DIALOGS.ARCHIVE);
    setTeamToUpdate(rowTeam);
  };

  const { customFieldColumns } = useGetCustomFieldColumns({
    recordType: RecordTypes.team,
  });

  const columns = useMemo<ExtendedGridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        description: handleReplaceWithCustomTeamName('Name of this team'),
        type: 'string',
        flex: 0.5,
        editable: true,
        inlineEditRenderCell: true,
        useMutation: useUpdateTeamMutation,
        editPermissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
          accessLevel: appPermissionAccessLevels.edit,
        },
        permissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
        },
      },
      {
        field: 'typeName',
        headerName: handleReplaceWithCustomTeamName('Team Type'),
        description: handleReplaceWithCustomTeamName('Type of this team'),
        type: 'string',
        flex: 0.4,
        editable: true,
        inlineEditRenderCell: true,
        useMutation: useUpdateTeamMutation,
        mutationInputElement: EditInlineTeamTypeAutocomplete,
        getMutationDefaultValue: (params: GridRenderEditCellParams) =>
          params.row.typeId,
        mutationPreSubmit: (data: FieldValues) => ({
          teamType: _.get(data, 'typeName'),
        }),
        valueGetter: (params: GridValueGetterParams) =>
          params.row.teamType?.name,
        editPermissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
          accessLevel: appPermissionAccessLevels.edit,
        },
        permissionScope: {
          app: ALL_APP_IDS.PROPERTIES,
        },
      },
      ...(otherOrgs > 0
        ? [
            {
              field: 'org',
              headerName: 'Org',
              description: handleReplaceWithCustomTeamName(
                'Org associated with this team'
              ),
              type: 'string',
              flex: 0.5,
              valueGetter: (params: GridValueGetterParams) =>
                orgFromUserOrgId(user, params.row.org)?.name,
            },
          ]
        : []),
      {
        field: 'relatedRecords',
        headerName: 'Related Records',
        description: handleReplaceWithCustomTeamName(
          'Related records for this team'
        ),
        type: 'string',
        minWidth: 200,
        flex: 0.5,
        hideable: true,
        renderCell: RelatedRecordRenderCell,
        filterOperators: relatedRecordsFilterOperators,
        sortComparator: relatedRecordsComparator,
        getApplyQuickFilterFn: getApplyFilterFnRelatedRecords,
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      {
        field: 'active',
        headerName: 'Active',
        description: handleReplaceWithCustomTeamName(
          'Indicates whether this team is active'
        ),
        type: 'boolean',
        flex: 0.3,
        hideable: true,
        valueGetter: (params: GridValueGetterParams) => !params.row.archived,
        renderCell: (params: GridRenderCellParams) =>
          BooleanIconRenderCell(params),
        permissionScope: { app: ALL_APP_IDS.PROPERTIES },
      },
      ...customFieldColumns,
    ],
    [customFieldColumns, otherOrgs, user, handleReplaceWithCustomTeamName]
  );

  return (
    <>
      <ExtendedDatagrid<TeamLite>
        loading={isLoadingTeams}
        noRows={handleReplaceWithCustomTeamName('No Teams found.')}
        gridName={DatagridNames.teams}
        rows={teams ?? []}
        columns={columns}
        fullCard
        initialVisibleColumns={[
          'name',
          'typeName',
          'org',
          'relatedRecords',
          ..._.map(customFieldColumns, 'field'),
        ]}
        initialFilter={{
          filterModel: {
            items: [
              {
                columnField: 'active',
                operatorValue: 'is',
                value: 'true',
              },
            ],
          },
        }}
        editable
        editLocked={editDisabled}
        newRecordButton={{
          label: handleReplaceWithCustomTeamName('New Team'),
          onClick: () => openDialog(TEAMS_DIALOGS.CREATE),
        }}
        rowActions={
          [
            {
              Icon: IconPencil,
              getLabel: () => handleReplaceWithCustomTeamName('Edit Team'),
              permissionScope: {
                app: ALL_APPS.PROPERTIES.id,
                accessLevel: appPermissionAccessLevels.edit,
              },
              getRowTooltip: () =>
                handleReplaceWithCustomTeamName('Update this team'),
              color: 'primary',
              disabled: editDisabled,
              onRowClick: (row) => {
                handleOpenUpdateTeamDialog(row as TeamLite);
              },
            },
            {
              getRowIcon: (row) =>
                row?.archived ? IconArchiveOff : IconArchive,
              getLabel: (row) =>
                handleReplaceWithCustomTeamName(
                  row?.archived ? 'Mark Active' : 'Mark Inactive'
                ),
              permissionScope: {
                app: ALL_APPS.PROPERTIES.id,
                accessLevel: appPermissionAccessLevels.edit,
              },
              getRowTooltip: (row) =>
                handleReplaceWithCustomTeamName(
                  `Mark this team ${row?.archived ? 'active' : 'inactive'}`
                ),
              color: 'primary',
              disabled: editDisabled,
              onRowClick: (row) => {
                handleOpenArchiveDialog(row as TeamLite);
              },
            },
          ] as RowAction<TeamLite>[]
        }
      />
      <CreateTeamDialog
        dialogOpen={currentDialog === TEAMS_DIALOGS.CREATE}
        onClose={closeDialog}
      />
      {teamToUpdate && (
        <UpdateTeamDialog
          dialogOpen={currentDialog === TEAMS_DIALOGS.UPDATE}
          onClose={closeDialog}
          teamToUpdate={teamToUpdate}
        />
      )}
      {teamToUpdate && (
        <ArchiveDialog
          idToUpdate={teamToUpdate.id}
          archived={teamToUpdate.archived}
          dialogOpen={currentDialog === TEAMS_DIALOGS.ARCHIVE}
          onClose={closeDialog}
          actionType={ManagementActionTypes.team}
        />
      )}
    </>
  );
};

export default TeamsTable;
